import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type PopupImportResolutionField = "funnel" | "page";

type Ctx = {
  getMessage: (rowIndex: number, field: PopupImportResolutionField) => string;
  setMessage: (
    rowIndex: number,
    field: PopupImportResolutionField,
    message: string
  ) => void;
  clearMessage: (rowIndex: number, field: PopupImportResolutionField) => void;
  clearRow: (rowIndex: number) => void;
  clearAllMessages: () => void;
};

const keyOf = (rowIndex: number, field: PopupImportResolutionField) =>
  `${rowIndex}.${field}`;

const PopupImportResolutionContext = createContext<Ctx | null>(null);

export const PopupImportResolutionProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [messages, setMessages] = useState<Record<string, string>>({});

  const getMessage = useCallback(
    (rowIndex: number, field: PopupImportResolutionField) =>
      messages[keyOf(rowIndex, field)] ?? "",
    [messages]
  );

  const setMessage = useCallback(
    (rowIndex: number, field: PopupImportResolutionField, message: string) => {
      const k = keyOf(rowIndex, field);
      setMessages((prev) => ({ ...prev, [k]: message }));
    },
    []
  );

  const clearMessage = useCallback(
    (rowIndex: number, field: PopupImportResolutionField) => {
      const k = keyOf(rowIndex, field);
      setMessages((prev) => {
        if (!(k in prev)) return prev;
        const next = { ...prev };
        delete next[k];
        return next;
      });
    },
    []
  );

  const clearRow = useCallback((rowIndex: number) => {
    setMessages((prev) => {
      const next = { ...prev };
      delete next[keyOf(rowIndex, "funnel")];
      delete next[keyOf(rowIndex, "page")];
      return next;
    });
  }, []);

  const clearAllMessages = useCallback(() => {
    setMessages({});
  }, []);

  const value = useMemo(
    () => ({
      getMessage,
      setMessage,
      clearMessage,
      clearRow,
      clearAllMessages,
    }),
    [getMessage, setMessage, clearMessage, clearRow, clearAllMessages]
  );

  return (
    <PopupImportResolutionContext.Provider value={value}>
      {children}
    </PopupImportResolutionContext.Provider>
  );
};

export function usePopupImportResolution(): Ctx {
  const v = useContext(PopupImportResolutionContext);
  if (!v) {
    throw new Error(
      "usePopupImportResolution must be used within PopupImportResolutionProvider"
    );
  }
  return v;
}
